import { TrendingUp, TrendingDown, Minus, AlertTriangle } from "lucide-react";

const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

/**
 * CompareToggle — Petit switch "vs N-1" (année précédente).
 * Stateless ; le parent gère le state booléen.
 */
export function CompareToggle({ enabled, onChange, testId = "compare-yoy-toggle" }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      data-testid={testId}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-colors ${
        enabled
          ? "bg-[#f7c935]/15 border-[#f7c935]/40 text-[#f7c935]"
          : "border-white/10 text-slate-400 hover:text-white hover:border-white/20"
      }`}
    >
      <TrendingUp className="w-3 h-3" />
      vs N-1
    </button>
  );
}

/**
 * Delta — affiche la variation entre `current` et `previous`.
 * Si `inverted=true`, une baisse est affichée en vert (utile pour les charges/dépenses).
 */
export function Delta({ current, previous, inverted = false, testId }) {
  const cur = Number(current || 0);
  const prev = Number(previous || 0);
  const diff = cur - prev;
  const pct = prev !== 0 ? (diff / Math.abs(prev)) * 100 : (cur !== 0 ? 100 : 0);

  if (prev === 0 && cur === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500" data-testid={testId}>
        <Minus className="w-2.5 h-2.5" /> —
      </span>
    );
  }

  const goodWhenUp = !inverted;
  const isUp = diff > 0;
  const isFavorable = isUp ? goodWhenUp : !goodWhenUp;
  const color = diff === 0 ? "#94a3b8" : (isFavorable ? "#10b981" : "#ef4444");
  const Icon = diff === 0 ? Minus : (isUp ? TrendingUp : TrendingDown);

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold tabular-nums"
      style={{ color }}
      data-testid={testId}
      title={`N-1: ${fmt(prev)} → N: ${fmt(cur)}`}
    >
      <Icon className="w-2.5 h-2.5" />
      {diff >= 0 ? "+" : ""}{pct.toFixed(0)}%
    </span>
  );
}

export default CompareToggle;

/**
 * AnomaliesAlert — Affiche les anomalies detectees par le backend (categories
 * en hausse anormale, encaissements en retard, etc.)
 */
export function AnomaliesAlert({ anomalies, testId = "anomalies-alert" }) {
  if (!anomalies || !anomalies.length) return null;
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-bold text-amber-400">Alertes pilotage ({anomalies.length})</h3>
      </div>
      <ul className="space-y-1.5">
        {anomalies.map((a, idx) => (
          <li key={idx} className="flex items-start gap-2 text-xs" data-testid={`anomaly-${a.type}-${idx}`}>
            <span className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${a.severity === "high" ? "bg-red-500" : "bg-amber-500"}`} />
            <span className="flex-1" style={{ color: "var(--text-primary)" }}>
              {a.type === "category_spike" && (
                <><strong>{a.label}</strong> : <span className="text-red-400 font-bold">+{a.delta_pct}%</span> vs N-1 ({fmt(a.previous)} → {fmt(a.current)})</>
              )}
              {a.type === "financement_drop" && (
                <><strong>{a.label}</strong> : <span className="text-red-400 font-bold">{a.delta_pct}%</span> vs N-1 ({fmt(a.previous)} → {fmt(a.current)})</>
              )}
              {a.type === "late_payments" && (
                <><strong>{a.label}</strong> — Total du : <span className="text-red-400 font-bold">{fmt(a.total_overdue)}</span></>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
